/**
 * DOConnection — the room-do WebSocket transport. This is the seam that REPLACES
 * spot-client's `xmpp-connection` (+ the P2P signaling path): a single WebSocket
 * to the per-room Durable Object, speaking the frozen `ControlEnvelope` union
 * instead of XMPP MUC presence/IQ. No strophe, no prosody, no XMPP.
 *
 * Responsibilities (the low-level half of the transport):
 *   - open `wss://<roomDo>/rooms/:roomId/connect?token=...&endpointId=...`
 *     (the access token rides on `?token=` because a browser WS cannot set the
 *     Authorization header — room-do accepts it there);
 *   - send the `hello` frame on open (endpoint identity + clientType);
 *   - parse inbound frames as `ControlEnvelope` and hand them to `onFrame`;
 *   - keep the room warm with a periodic `ping` (room-do auto-responds `pong`);
 *   - **survive drops**: a WebSocket to a Durable Object is NOT a permanent pipe —
 *     it closes on DO redeploys, edge resets, hibernation transitions, and wifi
 *     roams. After a connection that opened successfully drops, we transparently
 *     reconnect with exponential backoff (re-sending `hello` each time), so a
 *     transient blip no longer kills the TV<->Remote link. Only a server-intentional
 *     close (conflict / unauthorized) or exhausting the retry budget is terminal.
 *   - expose `send` / `close` and open/reconnect/close/error callbacks.
 *
 * The higher-level command/ack/presence semantics live in RemoteControlService.
 */

import type { ClientType, ControlEnvelope } from '@spaces/contracts';
import { log } from '../log.js';

export interface DOConnectionOptions {
    /** room-do base URL (http/https); converted to ws/wss. */
    roomDoUrl: string;
    /** The room to connect to (its DO instance name). */
    roomId: string;
    /** This endpoint's stable device id. */
    endpointId: string;
    /** Room-scoped access token, sent as `?token=` (WS can't set headers). */
    accessToken: string;
    /** TV / remote-perm / remote-temp. */
    clientType: ClientType;
    /** The short rotating code a Remote used (carried on the hello). */
    joinCode?: string;
    /**
     * Max reconnect attempts after an established connection drops. Defaults to
     * {@link DEFAULT_MAX_RECONNECT_ATTEMPTS}. Pass `Infinity` for an always-on
     * surface (the TV is a wall display — it should retry forever).
     */
    maxReconnectAttempts?: number;
    /**
     * Optional: mint a fresh access token before each (re)connect attempt. The TV
     * passes this (it holds a 30-day refresh token, so it can always re-mint the
     * 1-hour access token); the Remote omits it and reuses its access token, which
     * is valid for the in-session blip we care about.
     */
    refreshAccessToken?: () => Promise<string>;
    onFrame: (frame: ControlEnvelope) => void;
    /** The initial connection opened (fires once, on the first successful open). */
    onOpen?: () => void;
    /** A drop occurred and a reconnect is scheduled (NOT terminal). */
    onReconnecting?: (attempt: number, delayMs: number) => void;
    /** A reconnect attempt succeeded — the link is live again. */
    onReconnected?: () => void;
    /** Terminal: the socket is gone for good (server-intentional close, or retries exhausted). */
    onClose?: (code: number, reason: string) => void;
    onError?: () => void;
}

const PING_INTERVAL_MS = 25_000;
const BASE_RECONNECT_MS = 1_000;
const MAX_RECONNECT_MS = 15_000;
const DEFAULT_MAX_RECONNECT_ATTEMPTS = 8;

/**
 * Close codes that mean the SERVER decided to end this socket — never retry these
 * (reconnecting would just loop into the same rejection). 1000 = a deliberate
 * room-do close (e.g. a `conflict`); 1008 = policy/auth. Everything else (1006
 * abnormal, 1001 going-away, 1011/1012/1013) is a recoverable drop.
 */
function isTerminalCloseCode(code: number): boolean {
    return code === 1000 || code === 1008;
}

/** Convert an http(s) base URL to a ws(s) URL. */
function toWsBase(httpUrl: string): string {
    return httpUrl.replace(/^http/i, match => (match.toLowerCase() === 'https' ? 'wss' : 'ws'));
}

export class DOConnection {
    private readonly options: DOConnectionOptions;
    private readonly maxReconnectAttempts: number;
    private ws: WebSocket | null = null;
    private pingTimer: ReturnType<typeof setInterval> | null = null;
    private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    private closedByUs = false;
    /** Consecutive failed reconnect attempts (reset to 0 on a successful open). */
    private attempt = 0;
    /** Current access token (mutable: refreshed before each reconnect when a refresher is set). */
    private accessToken: string;

    constructor(options: DOConnectionOptions) {
        this.options = options;
        this.accessToken = options.accessToken;
        this.maxReconnectAttempts = options.maxReconnectAttempts ?? DEFAULT_MAX_RECONNECT_ATTEMPTS;
    }

    /**
     * Open the socket and resolve once it is open and the `hello` has been sent.
     * If the FIRST attempt fails, this rejects (so pairing/provisioning reports the
     * failure) — the background retry loop only engages after a successful open.
     */
    open(): Promise<void> {
        return this.connectOnce(false);
    }

    /** Send a control envelope frame. No-op if the socket is not open. */
    send(frame: ControlEnvelope): void {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify(frame));
        }
    }

    /** Close the socket (intentional — suppresses the reconnect loop). */
    close(): void {
        this.closedByUs = true;
        this.stopPing();
        this.cancelReconnect();
        this.ws?.close(1000, 'client closed');
        this.ws = null;
    }

    /**
     * One connect attempt. Resolves on `open`; for the INITIAL attempt, rejects if
     * the socket closes before opening. For a RECONNECT attempt, a close-before-open
     * never rejects — it just schedules the next retry (via the close handler).
     */
    private async connectOnce(isReconnect: boolean): Promise<void> {
        // Mint a fresh access token before reconnecting, if a refresher was provided.
        // A refresh failure is non-fatal: fall back to the last token and let the
        // attempt (and its close handler) decide whether to keep retrying.
        if (isReconnect && this.options.refreshAccessToken) {
            try {
                this.accessToken = await this.options.refreshAccessToken();
            } catch (err) {
                log.warn('DOConnection token refresh failed; reusing existing token', { error: String(err) });
            }
        }

        const { roomId, endpointId } = this.options;
        const url = this.buildUrl();

        return new Promise<void>((resolve, reject) => {
            let openedHere = false;
            const ws = new WebSocket(url);

            this.ws = ws;

            ws.addEventListener('open', () => {
                openedHere = true;
                this.attempt = 0;
                log.log('DOConnection open', { roomId, endpointId, clientType: this.options.clientType, reconnect: isReconnect });
                this.sendHello();
                this.startPing();

                if (isReconnect) {
                    this.options.onReconnected?.();
                } else {
                    this.options.onOpen?.();
                }

                resolve();
            });

            ws.addEventListener('message', (event: MessageEvent) => {
                let frame: ControlEnvelope;

                try {
                    frame = JSON.parse(String(event.data)) as ControlEnvelope;
                } catch {
                    return; // non-JSON (e.g. a stray pong) — not part of the typed protocol
                }

                this.options.onFrame(frame);
            });

            ws.addEventListener('error', () => {
                // Errors always precede a close; demote to warn and let `close` drive
                // the retry/terminal decision so we don't double-handle.
                log.warn('DOConnection error', { roomId });
                this.options.onError?.();
            });

            ws.addEventListener('close', (event: CloseEvent) => {
                this.stopPing();
                this.ws = null;
                log.log('DOConnection close', { code: event.code, reason: event.reason, byUs: this.closedByUs });

                if (this.closedByUs) {
                    if (!openedHere) {
                        reject(new Error('connection closed before open'));
                    }

                    return;
                }

                // The initial attempt failing (never opened, not a reconnect) is a hard
                // error the caller surfaces — no background retry for a first connect.
                if (!isReconnect && !openedHere) {
                    reject(new Error(`room-do socket closed before open (${event.code})`));

                    return;
                }

                this.handleDrop(event.code, event.reason);
            });
        });
    }

    /** Decide: schedule a reconnect, or give up (terminal). */
    private handleDrop(code: number, reason: string): void {
        if (isTerminalCloseCode(code) || this.attempt >= this.maxReconnectAttempts) {
            log.warn('DOConnection terminal', { code, reason, attempts: this.attempt });
            this.options.onClose?.(code, reason);

            return;
        }

        const delay = this.backoffDelay(this.attempt);

        this.attempt += 1;
        log.warn('DOConnection reconnecting', { attempt: this.attempt, delayMs: delay, code });
        this.options.onReconnecting?.(this.attempt, delay);

        this.cancelReconnect();
        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null;
            // Reconnect attempts never reject the caller — swallow and let this
            // attempt's own close handler reschedule on failure.
            void this.connectOnce(true).catch(() => undefined);
        }, delay);
    }

    /** Exponential backoff with ±20% jitter, capped at MAX_RECONNECT_MS. */
    private backoffDelay(attempt: number): number {
        const exp = Math.min(MAX_RECONNECT_MS, BASE_RECONNECT_MS * 2 ** attempt);
        const jitter = exp * 0.2 * (Math.random() * 2 - 1);

        return Math.max(BASE_RECONNECT_MS, Math.round(exp + jitter));
    }

    private buildUrl(): string {
        const { roomDoUrl, roomId, endpointId } = this.options;
        const base = toWsBase(roomDoUrl).replace(/\/+$/, '');

        return `${base}/rooms/${encodeURIComponent(roomId)}/connect`
            + `?token=${encodeURIComponent(this.accessToken)}&endpointId=${encodeURIComponent(endpointId)}`;
    }

    private sendHello(): void {
        this.send({
            kind: 'hello',
            endpointId: this.options.endpointId,
            clientType: this.options.clientType,
            ...(this.options.joinCode ? { joinCode: this.options.joinCode } : {})
        });
    }

    private startPing(): void {
        this.stopPing();
        this.pingTimer = setInterval(() => {
            if (this.ws?.readyState === WebSocket.OPEN) {
                this.ws.send('ping'); // room-do setWebSocketAutoResponse('ping','pong')
            }
        }, PING_INTERVAL_MS);
    }

    private stopPing(): void {
        if (this.pingTimer) {
            clearInterval(this.pingTimer);
            this.pingTimer = null;
        }
    }

    private cancelReconnect(): void {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
    }
}
